/**
 * A module for functions related to ad slot processing
 */

import { readConfigPath } from "../../../utils/js/configMgr.js";
import {
  getBreakpoint,
  getElemWidthHeight,
  getShape,
  isSameSize,
} from "../../../utils/js/utils.js";
import { logFeature } from "../../../../scriptsCore/js/logging.js";

export {
  getEmptySlots,
  getResizedSlots,
  getAllSlots,
} from "./getSlotsFromDocument.js";

const STANDARD_SIZES = [
  [300, 250],
  [300, 600],
  [728, 90],
  [970, 250],
  [320, 50],
];

const log = (...args) => logFeature("ads", ...args);

// counters used for generating slot-id's programmatically, for lazy load
const slotsUsed = new Set();
let fakeSlotIndex = 10;

/**
 * Declare that (SPA) navigation has happened, and any new slot's that we create can now re-use lower-numbered indexes
 */
export function resetSlotIndexForRefresh() {
  fakeSlotIndex = 10;
  slotsUsed.clear();
}

/**
 * Checks if container and definitions in the config match and meet the requirements to be used as a fluid adslot
 */
export function isValidFluidSlot(container, w) {
  const [width, height] = getElemWidthHeight(container);

  return (
    container.classList.contains("fluid-ad") &&
    w === "fluid" &&
    width >= 10 &&
    height >= 10
  );
}

/**
 * Accepts an ad container HTMLElement and matches it up with some metaData from the ads config
 * @param {HTMLElement} container
 * @returns {object} metadata to be used for injecting an ad
 */
export function getSlotCfgForContainer(container) {
  if (container.classList.contains("collapse-slot")) {
    return [];
  }
  const [width, height] = getElemWidthHeight(container);
  log("--- ", width, height, [...container.classList].join(","));

  const matchingCfgs = getMatchingSlotConfigs(container);
  log(matchingCfgs.length, "matching so far...");

  const fakeHighest = { targeting: { index: "1000000" } };
  const lowestIndexMatch = matchingCfgs.reduce((acc, slotCfg) => {
    return parseInt(slotCfg.targeting.index, 10) <
      parseInt(acc.targeting.index, 10)
      ? slotCfg
      : acc;
  }, fakeHighest);

  if (lowestIndexMatch !== fakeHighest) {
    slotsUsed.add(lowestIndexMatch.slotId);
  }

  log(
    "lowestIndexMatch",
    lowestIndexMatch.slotId,
    [...container.classList].join(",")
  );

  const slotMeta = { ...lowestIndexMatch, container };
  return slotMeta;
}

/**
 * Find all the matching ad definitions in the config for a specified ad container HTMLElement.
 * Matches by size, shape, classNames, page geometry.
 * If no matching config is found, such as for lazy-load ads, one will be created.
 * @param {HTMLElement} container
 * @returns {array} an array of one or more objects containing metadata needed to inject an ad
 */
function getMatchingSlotConfigs(container) {
  const bp = getBreakpoint(readConfigPath("breakpoints"));
  const slotsCfg = readConfigPath(
    `advertisingConfig.slots.breakpoints.${bp}.gpt`
  );
  const [width, height] = getElemWidthHeight(container);
  const matchesFromConfig = slotsCfg.filter((slot) => {
    const sizeMatch = slot.sizes.some(
      ([w, h]) => (w <= width && h <= height) || isValidFluidSlot(container, w)
    );
    const shapeMatch = slot.sizes.some(([w, h]) => {
      return (
        getShape(width, height) === getShape(w, h) ||
        isValidFluidSlot(container, w)
      );
    });
    const posMatch = matchSlotConfigByPosition(slot, container);
    const unused = !slotsUsed.has(slot.slotId);
    if (sizeMatch && shapeMatch && posMatch && unused) {
      log("match", slot.slotId, [...container.classList].join(","));
      return true;
    }
    return false;
  });

  if (matchesFromConfig.length) {
    return matchesFromConfig;
  } else {
    // didn't find anything inthe config!
    // now what?
    if (STANDARD_SIZES.some(([w, h]) => w === width && h === height)) {
      return [createExtraSlot(container)];
    } else {
      return [];
    }
  }
}

/**
 * Check for a match between a ad config metadata object and a container HTMLElement,
 * based on classNames or page geometry.
 * @param {Object} slotCfg
 * @param {HTMLElement} container
 * @returns {boolean}
 */
function matchSlotConfigByPosition(slotCfg, container) {
  const lookup = {
    "sidebar-ad": ["div-gpt-ad-rh-"],
    "bottom-ad": ["-bottom-"],
    "top-ad": ["div-gpt-ad-top-slot"],
    "center-ad": [
      "div-gpt-ad-section-slot",
      "div-gpt-ad-in-article-slot",
      "div-gpt-ad-top-slot",
    ],
    "in-feed-ad": ["div-gpt-ad-in-feed-slot"],
  };
  const pos = getContainerPos(container);
  const idFragments = lookup[pos];

  return idFragments.some((str) => slotCfg.slotId.includes(str));
}

/**
 * Detect the position or type of slot based on className or page geometry
 * @param {HTMLElement} container
 * @returns {string}     "sidebar-ad", "bottom-ad", "top-ad", "section-ad", "article-ad", "in-feed-ad"
 */
function getContainerPos(container) {
  const lookup = [
    "sidebar-ad",
    "bottom-ad",
    "top-ad",
    "section-ad",
    "article-ad",
    "in-feed-ad",
  ];
  const posFromClass = lookup.find((className) =>
    container.classList.contains(className)
  );
  if (posFromClass) {
    return posFromClass;
  } else {
    // :-( use geometry
    const center = window.innerWidth / 2;
    const { left } = container.getBoundingClientRect();
    // TODO: this wont find top or bottom ads if not labelled
    return left > center ? "sidebar-ad" : "center-ad";
  }
}

/**
 * Creae a new slot config metadata object that was not defined inthe config.
 * For lazy-loaded ads
 * @param {HTMLElement} container
 * @returns {object} ad config metadata
 */
function createExtraSlot(container) {
  const posLookup = {
    "center-ad": "div-gpt-ad-section-slot-other",
    "sidebar-ad": "div-gpt-ad-rh-slot-other",
  };
  const pos = getContainerPos(container);
  const [width, height] = getElemWidthHeight(container);
  const targetingSizeLookup = {
    baseLayout: [
      {
        containerSize: [300, 250],
        sizes: [
          [300, 250],
          [300, 254],
        ],
      },
      {
        containerSize: [300, 600],
        sizes: [
          [300, 250],
          [300, 254],
          [300, 600],
        ],
      },
      {
        containerSize: [728, 90],
        sizes: [[728, 90]],
      },
    ],
    desktop: [
      {
        containerSize: [300, 250],
        sizes: [
          [300, 250],
          [300, 253],
        ],
      },
      {
        containerSize: [300, 600],
        sizes: [
          [300, 600],
          [300, 250],
          [300, 253],
        ],
      },
      {
        containerSize: [728, 90],
        sizes: [
          [728, 90],
          [728, 96],
          [728, 97],
        ],
      },
      {
        containerSize: [970, 250],
        sizes: [
          [970, 250],
          [728, 90],
        ],
      },
    ],
  };
  const bp = getBreakpoint(readConfigPath("breakpoints"));
  const sizes = targetingSizeLookup[bp]?.find((sizeObj) =>
    isSameSize(sizeObj.containerSize, [width, height])
  )?.sizes || [[width, height]];
  const slotName = `${posLookup[pos]}-${++fakeSlotIndex}`;
  return {
    slotId: slotName,
    targeting: {
      index: fakeSlotIndex,
      hivis: "n",
      opos: "bottom",
      slotName,
    },
    sizes,
  };
}
